import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HelpCircle, Wine, Sparkles, ArrowRight, Mail, Search } from 'lucide-react';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GUEST_FAQS, ORGANISER_FAQS } from '../data/faq';

/**
 * FAQPage — /faq
 * Two audiences (Guests + Organisers), accordion-style, mobile-first.
 * Includes inline FAQPage JSON-LD schema so Google can build a rich snippet.
 */

const buildFaqJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

const FaqAccordion = ({ items, testIdPrefix }) => (
  <Accordion type="single" collapsible className="w-full divide-y divide-[#E5E9E6] bg-white rounded-2xl border border-[#E5E9E6] shadow-sm">
    {items.map((item, i) => (
      <AccordionItem
        key={item.q}
        value={`${testIdPrefix}-${i}`}
        className="px-5 sm:px-6 border-b-0 first:rounded-t-2xl last:rounded-b-2xl"
        data-testid={`${testIdPrefix}-item-${i}`}
      >
        <AccordionTrigger
          className="text-left py-5 text-[#1F3D2B] font-medium text-base sm:text-[15px] hover:no-underline group"
          data-testid={`${testIdPrefix}-trigger-${i}`}
        >
          <span className="flex-1 pr-3 leading-snug">
            {item.q}
            {item.comingSoon && (
              <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider bg-[#C9A27A]/15 text-[#8A6A3D] border border-[#C9A27A]/30 rounded-full px-2 py-0.5 align-middle">
                Coming soon
              </span>
            )}
          </span>
        </AccordionTrigger>
        <AccordionContent
          className="text-[#4A5D50] text-sm leading-relaxed pb-5"
          data-testid={`${testIdPrefix}-content-${i}`}
        >
          {item.a}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const FAQPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'organisers' ? 'organisers' : 'guests';
  const [tab, setTab] = useState(initialTab);
  const [query, setQuery] = useState('');

  useEffect(() => {
    document.title = 'FAQ \u2013 VinePass';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.setAttribute('name', 'description');
    meta.setAttribute('content', 'Frequently asked questions about VinePass \u2014 buying tickets to wine farm events, refunds, organiser onboarding, payouts and booking fees.');
    if (!meta.parentNode) document.head.appendChild(meta);
  }, []);

  useEffect(() => {
    // keep the URL in sync so links like /faq?tab=organisers work and back-button is clean
    setSearchParams(tab === 'organisers' ? { tab: 'organisers' } : {}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const filtered = useMemo(() => {
    const src = tab === 'organisers' ? ORGANISER_FAQS : GUEST_FAQS;
    const q = query.trim().toLowerCase();
    if (!q) return src;
    return src.filter((it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q));
  }, [tab, query]);

  const jsonLd = useMemo(
    () => buildFaqJsonLd([...GUEST_FAQS, ...ORGANISER_FAQS]),
    [],
  );

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="faq-page">
      {/* Inline JSON-LD for Google FAQ rich result */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F1EFE7] to-[#FAFAF7] border-b border-[#E5E9E6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E5E9E6] text-xs tracking-[0.18em] uppercase text-[#4A5D50] mb-5">
            <HelpCircle className="w-3.5 h-3.5 text-[#C9A27A]" />
            Frequently Asked
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#112318] tracking-tight leading-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            How can we help?
          </h1>
          <p className="text-base sm:text-lg text-[#4A5D50] leading-relaxed max-w-2xl mx-auto">
            Everything you need to know about booking experiences on VinePass — and listing yours.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the FAQ…"
              className="pl-11 h-12 rounded-full bg-white border-[#E5E9E6] focus-visible:ring-[#1F3D2B]"
              data-testid="faq-search-input"
              aria-label="Search the FAQ"
            />
          </div>
        </div>
      </section>

      {/* Tabs + content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div
          role="tablist"
          aria-label="FAQ audience"
          className="inline-flex p-1 bg-white border border-[#E5E9E6] rounded-full mb-8 shadow-sm w-full sm:w-auto"
          data-testid="faq-tabs"
        >
          {[
            { id: 'guests', label: 'For Guests', icon: Wine },
            { id: 'organisers', label: 'For Organisers', icon: Sparkles },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => { setTab(id); setQuery(''); }}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-2.5 rounded-full text-sm font-medium transition-all ${
                tab === id
                  ? 'bg-[#1F3D2B] text-white shadow-sm'
                  : 'text-[#4A5D50] hover:text-[#1F3D2B]'
              }`}
              data-testid={`faq-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Active panel */}
        {tab === 'guests' && (
          <div role="tabpanel" data-testid="faq-panel-guests">
            <h2
              className="text-2xl sm:text-3xl font-semibold text-[#112318] mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              For Guests &amp; Ticket Buyers
            </h2>
            <p className="text-sm text-[#4A5D50] mb-6">
              Bookings, tickets, refunds and on-the-day questions.
            </p>
            {filtered.length === 0 ? (
              <p className="text-center text-stone-500 py-12" data-testid="faq-empty-guests">
                No questions match &ldquo;{query}&rdquo;. Try a different search term.
              </p>
            ) : (
              <FaqAccordion items={filtered} testIdPrefix="faq-guest" />
            )}

            {/* Guest CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
              <p className="text-sm text-[#4A5D50]">Ready to find your next experience?</p>
              <Link to="/events" data-testid="faq-guest-cta">
                <Button className="rounded-full bg-[#1F3D2B] hover:bg-[#172E20] text-white px-6 h-11">
                  Explore Experiences
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {tab === 'organisers' && (
          <div role="tabpanel" data-testid="faq-panel-organisers">
            <h2
              className="text-2xl sm:text-3xl font-semibold text-[#112318] mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              For Organisers &amp; Wine Farms
            </h2>
            <p className="text-sm text-[#4A5D50] mb-6">
              Listing your event, payouts, fees, team scanning and onboarding.
            </p>
            {filtered.length === 0 ? (
              <p className="text-center text-stone-500 py-12" data-testid="faq-empty-organisers">
                No questions match &ldquo;{query}&rdquo;. Try a different search term.
              </p>
            ) : (
              <FaqAccordion items={filtered} testIdPrefix="faq-organiser" />
            )}

            {/* Organiser CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
              <p className="text-sm text-[#4A5D50]">Run wine farm events or curated experiences?</p>
              <Link to="/register?role=organiser" data-testid="faq-organiser-cta">
                <Button className="rounded-full bg-[#1F3D2B] hover:bg-[#172E20] text-white px-6 h-11">
                  List Your Event
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Still need help? */}
      <section className="bg-white border-t border-[#E5E9E6]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <Mail className="w-6 h-6 text-[#C9A27A] mx-auto mb-3" />
          <h2
            className="text-2xl sm:text-3xl font-semibold text-[#112318] mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Still have a question?
          </h2>
          <p className="text-[#4A5D50] mb-6 leading-relaxed">
            Our team replies to most enquiries within one working day.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="faq-still-need-help">
            <a href="mailto:tickets@vinepass.co.za">
              <Button variant="outline" className="rounded-full border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white h-11 px-6">
                Email Ticket Support
              </Button>
            </a>
            <a href="mailto:info@vinepass.co.za">
              <Button variant="outline" className="rounded-full border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white h-11 px-6">
                Email General Enquiries
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
